import { createContext, useContext, onMount, ParentComponent } from "solid-js";
import { tooltipStore } from "../../stores/tooltipStore";

export interface TooltipContextValue {
  /** Check if a tooltip has been dismissed */
  isDismissed: (id: string) => boolean;
  /** Dismiss a tooltip */
  dismiss: (id: string) => void;
  /** Reset all dismissed tooltips */
  resetAll: () => void;
}

const TooltipContext = createContext<TooltipContextValue>();

/**
 * Provider component for the tooltip system.
 * Initializes the tooltip store and provides tooltip management functions to children.
 *
 * Wrap your app root with this provider:
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <App />
 * </TooltipProvider>
 * ```
 */
export const TooltipProvider: ParentComponent = (props) => {
  onMount(() => {
    tooltipStore.init();
  });

  const value: TooltipContextValue = {
    isDismissed: tooltipStore.isDismissed,
    dismiss: tooltipStore.dismiss,
    resetAll: tooltipStore.resetAll,
  };

  return (
    <TooltipContext.Provider value={value}>
      {props.children}
    </TooltipContext.Provider>
  );
};

/**
 * Hook to access tooltip management functions.
 * Must be used within a TooltipProvider.
 *
 * @example
 * ```tsx
 * const tooltips = useTooltips();
 *
 * const handleDismiss = () => {
 *   tooltips.dismiss('my-tooltip-id');
 * };
 * ```
 */
export function useTooltips(): TooltipContextValue {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error("useTooltips must be used within a TooltipProvider");
  }
  return context;
}
